// ============================================================================
// The saves the building screens make, in one place (item 944, TCSD-0188).
//
// Each function here is the write a building screen already made inline —
// moved, not rewritten — so the screen and Ask AI's building card call the
// SAME door. The screens' behaviour is unchanged: same table or function, same
// arguments, same error handling (each returns or throws exactly what the inline
// code did) — except that the RAG and contact UPDATEs now ask for their rows
// back, so a change the database refused by row rule says so instead of looking
// saved (TCSD-0168). A new building write belongs here, not in a component.
//
//   Stop Service .............. pauseFacility / resumeFacility / endFacility
//   Termination record ........ terminationStep
//   Account manager ........... assignFacilityAm
//   RAG (modal and popover) ... saveRag
//   Points of contact ......... the contact functions
//   Feedback form ............. saveFeedback, feedbackFlagDefaults
//   Facility details card ..... saveFacilityDetailsFields, planDetailsPatch
//   Account Summary card ...... summaryColumnsPatch (the save is saveFieldsOnce)
// ============================================================================

import { supabase } from '../../../shared/supabase';

type RpcError = { message: string } | null;

/** ⛔ TCSD-0168: an UPDATE the database refuses by row rule returns no error and
 *  no rows. Each update here asks for its rows back; none back is a failure. */
const NOT_SAVED: RpcError = {
  message: "That didn't save — the database did not let this change through (you may not have permission to change it here).",
};

// ── Stop Service (stop-service.tsx) ─────────────────────────────────────────

export interface PauseArgs {
  p_facility_id: string;
  p_pause_reason_code: string;
  p_expected_resume_date?: string;
  p_note?: string;
  p_attribution?: string;
  p_relationship_intent?: string;
}
export interface ResumeArgs {
  p_facility_id: string;
  p_note?: string;
}
export interface EndArgs {
  p_facility_id: string;
  p_end_reason_code: string;
  p_final_service_date: string;
  p_note?: string;
  p_attribution?: string;
}

/** rpc_pause_facility. Optional arguments are OMITTED, never sent as null. Throws the RPC's error. */
export async function pauseFacility(args: PauseArgs): Promise<unknown> {
  const { data, error } = await supabase.rpc('rpc_pause_facility', args);
  if (error) throw error;
  return data;
}

/** rpc_resume_facility. Throws the RPC's error. */
export async function resumeFacility(args: ResumeArgs): Promise<unknown> {
  const { data, error } = await supabase.rpc('rpc_resume_facility', args);
  if (error) throw error;
  return data;
}

/** rpc_end_facility — opens the termination. Throws the RPC's error. */
export async function endFacility(args: EndArgs): Promise<unknown> {
  const { data, error } = await supabase.rpc('rpc_end_facility', args);
  if (error) throw error;
  return data;
}

// ── The termination record (termination-record.tsx) ─────────────────────────

export type TerminationFn =
  | 'rpc_ack_account_termination'
  | 'rpc_set_account_termination_final_date'
  | 'rpc_cancel_account_termination';

/** One step on an open termination. Throws the RPC's error. */
export async function terminationStep(fn: TerminationFn, args: Record<string, unknown>): Promise<unknown> {
  const { data, error } = await supabase.rpc(fn, args as never);
  if (error) throw error;
  return data;
}

// ── Account manager (account-manager-field.tsx, the Account Summary card) ───

/** assign_facility_am() — the one door for a building's account manager. */
export async function assignFacilityAm(facilityId: string, eid: string): Promise<{ error: RpcError }> {
  const { error } = await supabase.rpc('assign_facility_am', {
    p_facility_id: facilityId,
    p_eid: eid,
  });
  return { error };
}

// ── RAG (rag-edit-modal.tsx, rag-edit-popover.tsx) ──────────────────────────

/** The RAG and its four attribution columns, in one UPDATE. */
export async function saveRag(input: {
  facilityId: string;
  rag: string;
  category: string | null;
  reason: string | null;
  setByEid: string | null;
}): Promise<{ error: RpcError }> {
  const payload: Record<string, unknown> = {
    account_status_rag: input.rag,
    rag_category: input.category,
    rag_reason: input.reason,
    rag_set_at: new Date().toISOString(),
    rag_set_by: input.setByEid,
  };
  const { data, error } = await supabase
    .from('facilities')
    .update(payload)
    .eq('facility_id', input.facilityId)
    .select('facility_id');
  if (error) return { error };
  return { error: (data ?? []).length ? null : NOT_SAVED };
}

// ── Points of contact (poc-manager.tsx) ─────────────────────────────────────

/** Change one person's row at this building (role, title, phone, email, shown). */
export async function patchContactAssignment(
  assignmentId: string,
  fields: Record<string, unknown>,
): Promise<{ error: RpcError }> {
  const { data, error } = await supabase
    .from('contact_assignments')
    .update(fields)
    .eq('assignment_id', assignmentId)
    .select('assignment_id');
  if (error) return { error };
  return { error: (data ?? []).length ? null : NOT_SAVED };
}

/** Make (or stop making) someone the primary contact. One primary per scope:
 *  the others in the same building (or account) are cleared first. */
export async function setPrimaryContact(row: {
  assignment_id: string;
  facility_id: string | null;
  account_id: string | null;
  is_primary: boolean;
}): Promise<{ error: RpcError }> {
  let clr = supabase.from('contact_assignments').update({ is_primary: false });
  if (row.facility_id) clr = clr.eq('facility_id', row.facility_id);
  else if (row.account_id) clr = clr.eq('account_id', row.account_id);
  await clr.neq('assignment_id', row.assignment_id);
  const { data, error } = await supabase
    .from('contact_assignments')
    .update({ is_primary: !row.is_primary })
    .eq('assignment_id', row.assignment_id)
    .select('assignment_id');
  if (error) return { error };
  return { error: (data ?? []).length ? null : NOT_SAVED };
}

/** The contacts shown on the building card, in the manager's order. */
export function displayedContactsInOrder<T extends { is_displayed: boolean; display_order: number | null }>(rows: T[]): T[] {
  return rows
    .filter((x) => x.is_displayed)
    .sort((a, b) => (a.display_order ?? 9999) - (b.display_order ?? 9999));
}

export interface ContactSwap {
  a: { assignment_id: string; order: number };
  b: { assignment_id: string; order: number };
}

/** The manager's up / down arrow: which two shown rows swap places, and the
 *  order each takes. null when there is no neighbour that way (already first/last). */
export function contactSwapPlan<T extends { assignment_id: string; is_displayed: boolean; display_order: number | null }>(
  rows: T[],
  assignmentId: string,
  dir: -1 | 1,
): (ContactSwap & { neighbour: T }) | null {
  const displayed = displayedContactsInOrder(rows);
  const idx = displayed.findIndex((x) => x.assignment_id === assignmentId);
  if (idx < 0) return null;
  const r = displayed[idx];
  const swapWith = displayed[idx + dir];
  if (!swapWith) return null;
  const aOrder = r.display_order ?? idx + 1;
  const bOrder = swapWith.display_order ?? idx + dir + 1;
  return {
    a: { assignment_id: r.assignment_id, order: bOrder },
    b: { assignment_id: swapWith.assignment_id, order: aOrder },
    neighbour: swapWith,
  };
}

/** Swap the two rows' display_order (the manager's two UPDATEs). Returns the
 *  first error and how many of the two rows the database let through — an
 *  UPDATE refused by RLS returns no error and no rows (TCSD-0168). */
export async function swapContactOrder(plan: ContactSwap): Promise<{ error: RpcError; landed: number }> {
  let landed = 0;
  let firstError: RpcError = null;
  for (const side of [plan.a, plan.b]) {
    const { data, error } = await supabase
      .from('contact_assignments')
      .update({ display_order: side.order })
      .eq('assignment_id', side.assignment_id)
      .select('assignment_id');
    if (error && !firstError) firstError = error;
    landed += (data ?? []).length;
  }
  return { error: firstError, landed };
}

/** Put a person already on file on this building, shown on the card, last in order. */
export async function addExistingContact(
  facilityId: string,
  contactId: string,
  displayOrder: number,
): Promise<{ error: RpcError }> {
  const { error } = await supabase.from('contact_assignments').insert({
    contact_id: contactId,
    facility_id: facilityId,
    is_displayed: true,
    display_order: displayOrder,
    is_primary: false,
  });
  return { error };
}

export interface NewContactInput {
  first: string;
  last: string;
  title: string;
  mobile: string;
  email: string;
  role: string;
  scope: 'Facility' | 'Account';
  facilityId: string;
  accountId: string | null;
}

/** A brand-new person, and their row at this building (or account). The error
 *  says which half failed, as the form did. */
export async function createContactForFacility(
  input: NewContactInput,
): Promise<{ error: string | null; contactId: string | null }> {
  const { data: created, error: cErr } = await supabase
    .from('contacts')
    .insert({
      first_name: input.first.trim() || null,
      last_name: input.last.trim() || null,
      job_title: input.title.trim() || null,
      mobile: input.mobile.trim() || null,
      email: input.email.trim() || null,
      contact_role: input.role.trim() || null,
      scope_type: input.scope,
      facility_id: input.scope === 'Facility' ? input.facilityId : null,
      account_id: input.scope === 'Account' ? input.accountId : null,
    })
    .select('contact_id')
    .single();
  if (cErr || !created) {
    return { error: cErr?.message ?? 'Could not create the person.', contactId: null };
  }
  const contactId = (created as { contact_id: string }).contact_id;
  const { error: aErr } = await supabase.from('contact_assignments').insert({
    contact_id: contactId,
    account_id: input.scope === 'Account' ? input.accountId : null,
    facility_id: input.scope === 'Facility' ? input.facilityId : null,
    contact_role: input.role.trim() || null,
    job_title: input.title.trim() || null,
    assignment_email: input.email.trim() || null,
    assignment_mobile: input.mobile.trim() || null,
    is_displayed: true,
    is_primary: false,
  });
  if (aErr) return { error: `Person created, but linking failed: ${aErr.message}`, contactId };
  return { error: null, contactId };
}

/** Take someone off this building (audited, reversible). */
export async function removeContactAssignment(assignmentId: string): Promise<{ error: RpcError }> {
  const { error } = await supabase.rpc('remove_contact_assignment', {
    p_assignment_id: assignmentId,
  });
  return { error };
}

/** Merge a duplicate person into the one to keep. */
export async function mergeContactInto(
  sourceContactId: string,
  targetContactId: string,
): Promise<{ error: RpcError }> {
  const { error } = await supabase.rpc('merge_contact_into', {
    p_source_contact_id: sourceContactId,
    p_target_contact_id: targetContactId,
  });
  return { error };
}

// ── Feedback (feedback-form.tsx) ────────────────────────────────────────────

/** What the form pre-ticks from the sentiment; the person can still change it. */
export function feedbackFlagDefaults(sentiment: string): {
  counts_against_nmi: boolean;
  counts_toward_compliment: boolean;
} {
  return {
    counts_against_nmi: sentiment === 'Negative' || sentiment === 'Critical',
    counts_toward_compliment: sentiment === 'Positive',
  };
}

// Off the form 2026-09-03 (Ben, addendum 2D); still sent — see feedback-form.tsx.
export const FEEDBACK_FORM_CONSTANTS = {
  category: 'Cleaning_Quality',
  source: 'AM_Observation',
  status: 'Open',
  issue_count: 1,
} as const;

export interface FeedbackInput {
  facility_id: string;
  account_id: string | null;
  feedback_date: string;
  reported_by_employee_id: string | null;
  sentiment: string;
  description: string;
  counts_against_nmi: boolean;
  counts_toward_compliment: boolean;
}

/** The feedback form's save. Returns the new feedback id; throws when it did not land. */
export async function saveFeedback(input: FeedbackInput): Promise<string> {
  const payload = {
    facility_id: input.facility_id,
    account_id: input.account_id,
    feedback_date: input.feedback_date,
    reported_by_employee_id: input.reported_by_employee_id,
    reported_by_contact_id: null,
    sentiment: input.sentiment,
    category: FEEDBACK_FORM_CONSTANTS.category,
    description: input.description.trim(),
    issue_count: FEEDBACK_FORM_CONSTANTS.issue_count,
    resolved_count: 0,
    recurrence_count: 0,
    status: FEEDBACK_FORM_CONSTANTS.status,
    source: FEEDBACK_FORM_CONSTANTS.source,
    counts_against_nmi: input.counts_against_nmi,
    counts_toward_compliment: input.counts_toward_compliment,
  };
  // ⛔ THE `.select()` STAYS (TCSD-0168): a write refused by RLS returns no
  // error and no rows.
  const { data: inserted, error: insErr } = await supabase
    .from('facility_feedback')
    .insert(payload)
    .select('feedback_id')
    .single();
  if (insErr) throw insErr;
  if (!inserted?.feedback_id) {
    throw new Error(
      "That didn't save — you may not have permission to add feedback for this facility.",
    );
  }
  return inserted.feedback_id as string;
}

// ── Account Summary card (facility-detail.tsx) ──────────────────────────────

/** The plain columns the Account Summary card writes, trimmed, blank → null. */
export const SUMMARY_TEXT_COLUMNS = ['facility_name', 'facility_address', 'market_region', 'facility_type'] as const;

export function summaryColumnsPatch(
  draft: Record<string, string | number | null>,
): Record<string, string | null> {
  const patch: Record<string, string | null> = {};
  for (const key of SUMMARY_TEXT_COLUMNS) {
    if (key in draft) {
      const v = draft[key];
      patch[key] = v == null ? null : String(v).trim() || null;
    }
  }
  return patch;
}
